import React, { useContext } from "react";
import { Box, Typography, Fade } from "@mui/material";
import { Icon } from "@iconify/react";
import { AuthContext } from "../../context/AuthContextProvider";
import { RequireAuth } from "../../RequireAuth";

/* Theme-aligned colour palette (primary purple + status colours of this UI) */
const PALETTE = {
  purple: { color: "#5751E9", bg: "#ECEBFD" },
  green: { color: "#43A047", bg: "#E7F5E7" },
  amber: { color: "#E08F2C", bg: "#FDF1E1" },
  red: { color: "#F15B60", bg: "#FEECEC" },
  indigo: { color: "#4C6FFF", bg: "#EAEEFF" },
};

const formatAvgTime = (totalMinutes) => {
  if (!totalMinutes) return "-";

  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.floor(totalMinutes % 60);
  const seconds = Math.round((totalMinutes % 1) * 60);

  const parts = [];

  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0) parts.push(`${seconds}s`);

  return parts.join(" ");
};

/* Reusable SaaS-style stat card */
const StatCard = ({ icon, palette, label, value }) => (
  <Box
    sx={{
      position: "relative",
      overflow: "hidden",
      backgroundColor: "var(--app-surface)",
      border: "1px solid var(--app-border)",
      borderRadius: "16px",
      // boxShadow: "0 4px 12px rgba(16,24,40,0.08)",
      p: { xs: 2, md: 2.5 },
      display: "flex",
      alignItems: "center",
      gap: 1.75,
      height: "100%",
      // Fade-in only (the :hover below already uses transform, so we must not
      // animate transform on entrance or it would override the hover lift).
      opacity: 0,
      animation: "dashFadeIn .5s ease forwards",
      transition: " .2s ease, transform .2s ease",
      "&:hover": {
        // boxShadow: "0 8px 22px rgba(16,24,40,0.12)",
        transform: "translateY(-2px)",
      },
      // Faint graph-paper grid
      "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        backgroundImage: `linear-gradient(${palette.color} 1px, transparent 1px), linear-gradient(90deg, ${palette.color} 1px, transparent 1px)`,
        backgroundSize: "22px 22px",
        opacity: 0.05,
        pointerEvents: "none",
      },
      // Soft tinted corner glow
      "&::after": {
        content: '""',
        position: "absolute",
        top: -50,
        right: -50,
        width: 130,
        height: 130,
        borderRadius: "50%",
        background: `radial-gradient(circle, ${palette.color} 0%, transparent 70%)`,
        opacity: 0.1,
        pointerEvents: "none",
      },
    }}
  >
    {/* Large translucent watermark of the card's icon */}
    <Box
      sx={{
        position: "absolute",
        bottom: -14,
        right: -10,
        color: palette.color,
        opacity: 0.08,
        transform: "rotate(-12deg)",
        pointerEvents: "none",
        lineHeight: 0,
      }}
    >
      <Icon icon={icon} width={92} height={92} />
    </Box>

    <Box
      sx={{
        position: "relative",
        zIndex: 1,
        flexShrink: 0,
        width: 48,
        height: 48,
        borderRadius: "14px",
        backgroundColor: palette.bg,
        color: palette.color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon icon={icon} width={26} height={26} />
    </Box>

    <Box sx={{ position: "relative", zIndex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 13.5,
          fontWeight: 600,
          color: "var(--app-text-muted)",
          fontFamily: "Figtree",
          lineHeight: 1.3,
          mb: 0.25,
        }}
      >
        {label}
      </Typography>
      <Fade in>
        <Typography
          sx={{
            fontWeight: 800,
            color: "var(--app-text)",
            fontSize: { xs: "24px", md: "28px" },
            fontFamily: "Figtree",
            lineHeight: 1.1,
            whiteSpace: "nowrap",
          }}
        >
          {value}
        </Typography>
      </Fade>
    </Box>
  </Box>
);

const gridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(2, 1fr)",
    lg: "repeat(auto-fit, minmax(200px, 1fr))",
  },
  gap: { xs: 1.5, md: 2 },
  width: "100%",
};

const DashboardKPISection = ({ analyticsData }) => {
  const { user } = useContext(AuthContext);

  return (
    <>
      {user?.userType === "b2b-doctor" ? (
        <Box sx={gridSx}>
          <RequireAuth role={["b2b-doctor"]} permission={"canViewCases"}>
            <StatCard
              icon="solar:user-hand-up-bold-duotone"
              palette={PALETTE.purple}
              label="Cases Referred"
              value={
                analyticsData?.casesReferred > 0
                  ? analyticsData?.casesReferred
                  : "-"
              }
            />
          </RequireAuth>

          <RequireAuth role={["b2b-doctor"]} permission={"canViewCases"}>
            <StatCard
              icon="solar:check-circle-bold-duotone"
              palette={PALETTE.green}
              label="Cases Closed"
              value={
                analyticsData?.casesClosed > 0 ? analyticsData?.casesClosed : "-"
              }
            />
          </RequireAuth>

          <RequireAuth role={["b2b-doctor"]} permission={"canInterpreteReport"}>
            <StatCard
              icon="solar:inbox-in-bold-duotone"
              palette={PALETTE.indigo}
              label="Test Received"
              value={
                analyticsData?.testsReceived > 0
                  ? analyticsData?.testsReceived
                  : "-"
              }
            />
          </RequireAuth>

          <RequireAuth role={["b2b-doctor"]} permission={"canInterpreteReport"}>
            <StatCard
              icon="solar:clipboard-check-bold-duotone"
              palette={PALETTE.red}
              label="Tests Interpreted"
              value={
                analyticsData?.totalInterpretedCount > 0
                  ? analyticsData?.totalInterpretedCount
                  : "-"
              }
            />
          </RequireAuth>

          <RequireAuth role={["b2b-doctor"]} permission={"canInterpreteReport"}>
            <StatCard
              icon="solar:clock-circle-bold-duotone"
              palette={PALETTE.amber}
              label="Avg Interpretation Time"
              value={formatAvgTime(analyticsData?.avgInterpretationTime)}
            />
          </RequireAuth>
        </Box>
      ) : (
        <Box sx={gridSx}>
          {/* Tests Taken */}
          <StatCard
            icon="solar:heart-pulse-bold-duotone"
            palette={PALETTE.purple}
            label="Tests Taken"
            value={
              analyticsData?.totalTestsTaken > 0
                ? analyticsData.totalTestsTaken
                : "-"
            }
          />

          {/* Tests Interpreted */}
          <StatCard
            icon="solar:clipboard-check-bold-duotone"
            palette={PALETTE.green}
            label="Tests Interpreted"
            value={
              analyticsData?.interpretationStatusCounts
                ? Object.values(
                    analyticsData.interpretationStatusCounts,
                  ).reduce((sum, v) => sum + v, 0) || "-"
                : "-"
            }
          />

          {/* Avg Interpretation Time */}
          <StatCard
            icon="solar:clock-circle-bold-duotone"
            palette={PALETTE.amber}
            label="Avg Interpretation Time"
            value={formatAvgTime(analyticsData?.avgInterpretationTime)}
          />

          {/* Cases */}
          <StatCard
            icon="solar:folder-with-files-bold-duotone"
            palette={PALETTE.red}
            label="Cases"
            value={analyticsData?.cases > 0 ? analyticsData.cases : "-"}
          />
        </Box>
      )}
    </>
  );
};

export default DashboardKPISection;
